import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import equal from 'fast-deep-equal';
import { cn, Price } from '@divlab/divanui';
import { IconCart } from '@divlab/divanui/icons';
import { CSSTransition } from 'react-transition-group';

import * as Cart from '@Queries/Cart';
import * as PageProductStore from '@Stores/PageProduct';
import { useRelatedProducts } from '@Queries/RelatedProducts';
import { useDeps } from '@Contexts/DI';
import useTranslation from '@Queries/useTranslation';
import Button from '@UI/Button';
import styles from './StickyBuyButton.module.css';

import type { ButtonProps } from '@UI/Button';
import type { FC } from 'react';
import type { CartModalInputProduct } from '@Types/Cart';

export interface StickyBuyButtonProps extends ButtonProps {
  className?: string;
  visible?: boolean;
}

const StickyBuyButton: FC<StickyBuyButtonProps> = (props) => {
  const { className, visible = false, ...restProps } = props;
  const page = PageProductStore.useData();
  const { t } = useTranslation();
  const parameterValues = PageProductStore.getParameterValues();
  const defaultParameterValues = useRef(parameterValues);
  const [open, setOpen] = useState(false);
  const addToCart = Cart.useAddToCart();
  const { logger } = useDeps();
  const { data: relatedProducts } = useRelatedProducts(page.product.id);

  const isModifiedParameters = useMemo(() => {
    return !equal(defaultParameterValues.current, parameterValues);
  }, [parameterValues, defaultParameterValues]);

  const handleError = useCallback(
    (err: unknown) => {
      logger.log(err);
    },
    [logger],
  );

  const handleClick = useCallback(() => {
    if (addToCart.isPending || addToCart.isError) {
      return;
    }

    const products: CartModalInputProduct[] = [
      {
        isModular: page.isModular,
        shopProductId: page.product.id,
        parameterValues,
        isModifiedParameters,
      },
    ];

    const selectedRelatedProducts =
      relatedProducts?.products.filter((product) => product.quantity > 0) || [];

    selectedRelatedProducts.forEach((product) => {
      products.push({
        shopProductId: product.id,
        quantity: product.quantity,
        isModular: false,
      });
    });

    addToCart.mutate(
      {
        products,
        options: { isRelated: false },
        analyticsLabel: 'Карточка товара',
      },
      {
        onError: handleError,
      },
    );

    //TODO: удалить/вернуть после теста
    // ModalWindows.open('Cart', {
    //   products: [
    //     {
    //       isModular: page.isModular,
    //       shopProductId: page.product.id,
    //       parameterValues,
    //       isModifiedParameters,
    //     },
    //   ],
    //   analyticsLabel: 'Карточка товара',
    // });
  }, [
    addToCart,
    page.isModular,
    page.product.id,
    parameterValues,
    isModifiedParameters,
    relatedProducts?.products,
    handleError,
  ]);

  useEffect(() => {
    setOpen(visible);
  }, [visible]);

  return (
    <CSSTransition
      timeout={400}
      classNames={{
        enter: styles.enter,
        enterActive: styles.enterActive,
        enterDone: styles.enterDone,
        exit: styles.exit,
        exitActive: styles.exitActive,
        exitDone: styles.exitDone,
      }}
      unmountOnExit
      in={open}
    >
      <Button
        {...restProps}
        wide
        size='l'
        className={cn(styles.buyButton, styles.sizeBig, className)}
        onClick={handleClick}
        data-testid='mobile-buy-button'
      >
        <IconCart className={styles.iconBasket} />
        {t('ui.add-to-cart')}
        <Price className={styles.price} price={page.product.price.actual} />
      </Button>
    </CSSTransition>
  );
};

export default memo(StickyBuyButton);
